/**
 * Custom UX analytics — Firestore `uxEvents` + dual-write to Firebase Analytics.
 * Fire-and-forget from UI; failures must never break product flows.
 */

import {
  addDoc,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { logAnalyticsEvent } from '../../analytics/logAnalyticsEvent';
import { shouldSkipAnalytics } from '../../analytics/analyticsActor';
import {
  appendPathTrail,
  formatPathTrail,
  getLastAction,
  getOrCreateVisitId,
  isFirstVisitBrowser,
  setLastAction,
} from '../../analytics/firstVisit';

export const UX_EVENTS_COLLECTION = 'uxEvents';

export const UX_EVENT_NAMES = Object.freeze([
  'event_save',
  'event_unsave',
  'place_save',
  'place_unsave',
  'event_open',
  'event_impression',
  'place_open',
  'place_event_open',
  'place_nearby_open',
  'nav_tab',
  'drop_create',
  'drop_pickup',
  'friend_request_send',
  'chat_message_send',
  'push_permission_grant',
  'push_permission_deny',
  'category_filter',
  'onboarding_tip_shown',
  'onboarding_tip_dismissed',
  'onboarding_audience_set',
  'page_dwell',
  'first_visit_exit',
  'vibecheck_upload',
  'radio_close',
  'radio_show',
  'artist_open',
  'loot_box_open',
  'experiment_toggle',
  'intent_select',
  'walker_level_up',
  'giveaway_view',
  'giveaway_participate',
  'giveaway_reply',
]);

const NAME_SET = new Set(UX_EVENT_NAMES);
const TITLE_MAX = 80;
const ID_MAX = 120;
const PARAM_MAX = 64;
const PATH_TRAIL_MAX = 400;
export const DWELL_MIN_MS = 2000;
export const DWELL_MAX_MS = 30 * 60 * 1000;

/**
 * @param {unknown} value
 * @param {number} max
 * @returns {string|null}
 */
function clipString(value, max) {
  if (value == null || value === '') return null;
  const s = String(value).trim().slice(0, max);
  return s || null;
}

/**
 * Group detail routes for time-on-page aggregates.
 * @param {string} pathname
 * @returns {string}
 */
export function normalizePageKey(pathname) {
  const raw = String(pathname || '/').split('?')[0] || '/';
  const p = raw.length > 1 && raw.endsWith('/') ? raw.slice(0, -1) : raw;
  if (p === '/' || p === '') return '/';
  if (/^\/events\/[^/]+$/i.test(p)) return '/events/:eventId';
  if (/^\/places\/[^/]+$/i.test(p)) return '/places/:placeId';
  if (/^\/artists\/[^/]+$/i.test(p)) return '/artists/:slug';
  if (/^\/c\/[^/]+$/i.test(p)) return '/c/:token';
  if (p.startsWith('/adminhub')) return '/adminhub/*';
  if (p.startsWith('/partner')) return '/partner/*';
  if (p.startsWith('/game-maker')) return '/game-maker/*';
  return clipString(p, ID_MAX) || '/';
}

/**
 * @param {unknown} value
 * @returns {number|null}
 */
export function clampDurationMs(value) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.min(DWELL_MAX_MS, n);
}

/**
 * Build a Firestore-safe payload (no serverTimestamp until write).
 * @param {object} fields
 * @returns {object|null} null if name invalid
 */
export function buildUxEventPayload(fields = {}) {
  const name = String(fields.name || '').trim();
  if (!NAME_SET.has(name)) return null;

  const payload = {
    name,
    pathname: clipString(fields.pathname || '/', ID_MAX) || '/',
    uid: fields.uid ? String(fields.uid).slice(0, ID_MAX) : null,
  };

  const eventId = clipString(fields.eventId, ID_MAX);
  if (eventId) payload.eventId = eventId;
  const placeId = clipString(fields.placeId, ID_MAX);
  if (placeId) payload.placeId = placeId;
  const targetPlaceId = clipString(fields.targetPlaceId, ID_MAX);
  if (targetPlaceId) payload.targetPlaceId = targetPlaceId;
  const artistId = clipString(fields.artistId, ID_MAX);
  if (artistId) payload.artistId = artistId;
  const dropId = clipString(fields.dropId, ID_MAX);
  if (dropId) payload.dropId = dropId;
  const title = clipString(fields.title, TITLE_MAX);
  if (title) payload.title = title;
  const source = clipString(fields.source, PARAM_MAX);
  if (source) payload.source = source;
  const tab = clipString(fields.tab, PARAM_MAX);
  if (tab) payload.tab = tab;
  const type = clipString(fields.type, PARAM_MAX);
  if (type) payload.type = type;
  const via = clipString(fields.via, PARAM_MAX);
  if (via) payload.via = via;
  const surface = clipString(fields.surface, PARAM_MAX);
  if (surface) payload.surface = surface;
  const category = clipString(fields.category, PARAM_MAX);
  if (category) payload.category = category;

  const pageKey = clipString(fields.pageKey, ID_MAX);
  if (pageKey) payload.pageKey = pageKey;
  const visitId = clipString(fields.visitId, ID_MAX);
  if (visitId) payload.visitId = visitId;
  const lastAction = clipString(fields.lastAction, PARAM_MAX);
  if (lastAction) payload.lastAction = lastAction;
  const lastPageKey = clipString(fields.lastPageKey, ID_MAX);
  if (lastPageKey) payload.lastPageKey = lastPageKey;
  const pathTrail = clipString(fields.pathTrail, PATH_TRAIL_MAX);
  if (pathTrail) payload.pathTrail = pathTrail;

  const durationMs = clampDurationMs(fields.durationMs);
  if (durationMs != null && fields.durationMs != null && fields.durationMs !== '') {
    payload.durationMs = durationMs;
  }

  if (fields.firstVisit === true) payload.firstVisit = true;
  if (fields.isLastAction === true) payload.isLastAction = true;

  return payload;
}

/**
 * Params safe for GA4 (snake_case keys, primitives only).
 * @param {object} payload
 * @returns {Record<string, string|number|boolean>}
 */
export function uxEventToAnalyticsParams(payload) {
  if (!payload) return {};
  const params = {};
  if (payload.eventId) params.event_id = payload.eventId;
  if (payload.placeId) params.place_id = payload.placeId;
  if (payload.targetPlaceId) params.target_place_id = payload.targetPlaceId;
  if (payload.dropId) params.drop_id = payload.dropId;
  if (payload.title) params.item_title = payload.title;
  if (payload.source) params.source = payload.source;
  if (payload.tab) params.tab = payload.tab;
  if (payload.type) params.type = payload.type;
  if (payload.via) params.via = payload.via;
  if (payload.surface) params.surface = payload.surface;
  if (payload.category) params.category = payload.category;
  if (payload.pathname) params.pathname = payload.pathname;
  if (payload.pageKey) params.page_key = payload.pageKey;
  if (payload.visitId) params.visit_id = payload.visitId;
  if (payload.lastAction) params.last_action = payload.lastAction;
  if (payload.lastPageKey) params.last_page_key = payload.lastPageKey;
  if (payload.pathTrail) params.path_trail = payload.pathTrail;
  if (typeof payload.durationMs === 'number') params.duration_ms = payload.durationMs;
  if (payload.firstVisit === true) params.first_visit = 1;
  if (payload.isLastAction === true) params.is_last_action = 1;
  return params;
}

/**
 * Persist + dual-write GA. Never throws to callers.
 * Auto-tags first-visit sessions and updates in-memory lastAction / trail.
 * @param {object} fields
 * @returns {Promise<string|null>}
 */
export async function trackUxEvent(fields) {
  try {
    if (shouldSkipAnalytics()) return null;

    const enriched = { ...fields };
    if (typeof window !== 'undefined' && isFirstVisitBrowser()) {
      const visitId = getOrCreateVisitId();
      if (visitId && !enriched.visitId) enriched.visitId = visitId;
      if (enriched.firstVisit == null) enriched.firstVisit = true;
      const pageKey =
        enriched.pageKey ||
        normalizePageKey(
          enriched.pathname ||
            (typeof window !== 'undefined' ? window.location.pathname : '/')
        );
      if (!enriched.pageKey && pageKey) enriched.pageKey = pageKey;
      if (enriched.name && enriched.name !== 'first_visit_exit') {
        setLastAction({ name: enriched.name, pageKey });
      }
    }

    const base = buildUxEventPayload(enriched);
    if (!base) return null;
    const pathname =
      typeof window !== 'undefined' && !enriched.pathname
        ? window.location.pathname
        : base.pathname;
    const payload = {
      ...base,
      pathname: clipString(pathname, ID_MAX) || '/',
      createdAt: serverTimestamp(),
    };
    logAnalyticsEvent(payload.name, uxEventToAnalyticsParams(payload));
    // Firestore create requires request.auth (see firestore.rules). Guests with no
    // session would only get permission-denied noise — keep GA dual-write above.
    if (!auth?.currentUser) {
      return null;
    }
    if (!payload.uid && auth.currentUser.uid) {
      payload.uid = String(auth.currentUser.uid).slice(0, ID_MAX);
    }
    const ref = await addDoc(collection(db, UX_EVENTS_COLLECTION), payload);
    return ref.id;
  } catch (err) {
    console.warn('trackUxEvent failed:', err?.message || err);
    return null;
  }
}

/**
 * Record a page enter for first-visit trail (no Firestore write).
 * @param {string} pathname
 */
export function noteFirstVisitPageEnter(pathname) {
  if (typeof window === 'undefined' || !isFirstVisitBrowser()) return;
  getOrCreateVisitId();
  const pageKey = normalizePageKey(pathname);
  appendPathTrail(pageKey);
  setLastAction({ name: 'page_view', pageKey });
}

/**
 * Build fields for first_visit_exit from in-memory state.
 * @returns {object|null}
 */
export function buildFirstVisitExitFields() {
  if (typeof window === 'undefined' || !isFirstVisitBrowser()) return null;
  const visitId = getOrCreateVisitId();
  if (!visitId) return null;
  const last = getLastAction();
  const trail = formatPathTrail();
  return {
    name: 'first_visit_exit',
    firstVisit: true,
    isLastAction: true,
    visitId,
    pathTrail: trail || '/',
    lastAction: last?.name || 'page_view',
    lastPageKey: last?.pageKey || normalizePageKey(window.location.pathname),
    pageKey: last?.pageKey || normalizePageKey(window.location.pathname),
    pathname: window.location.pathname || '/',
  };
}

/**
 * Admin list — newest first.
 * @param {{ limitCount?: number }} [opts]
 * @returns {Promise<object[]>}
 */
export async function listUxEvents({ limitCount = 400 } = {}) {
  const q = query(
    collection(db, UX_EVENTS_COLLECTION),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Count events by `name`.
 * @param {object[]} rows
 * @returns {{ name: string, count: number }[]}
 */
export function aggregateUxEventCounts(rows) {
  const byName = new Map();
  (rows || []).forEach((row) => {
    const key = String(row.name || '').trim();
    if (!key) return;
    byName.set(key, (byName.get(key) || 0) + 1);
  });
  return Array.from(byName.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

/**
 * Top entity ids for a given event name (e.g. event_open → eventId).
 * @param {object[]} rows
 * @param {string} eventName
 * @param {'eventId'|'placeId'|'tab'|'category'|'dropId'|'pageKey'} idField
 * @param {{ limitCount?: number, titleField?: string }} [opts]
 * @returns {{ id: string, count: number, title: string|null }[]}
 */
export function aggregateUxTopIds(rows, eventName, idField, { limitCount = 20, titleField = 'title' } = {}) {
  const byId = new Map();
  (rows || []).forEach((row) => {
    if (row.name !== eventName) return;
    const id = String(row[idField] || '').trim();
    if (!id) return;
    const prev = byId.get(id) || { id, count: 0, title: null };
    prev.count += 1;
    if (!prev.title && row[titleField]) prev.title = String(row[titleField]);
    byId.set(id, prev);
  });
  return Array.from(byId.values())
    .sort((a, b) => b.count - a.count || a.id.localeCompare(b.id))
    .slice(0, limitCount);
}

/**
 * Human-readable duration for dwell totals.
 * @param {unknown} ms
 * @returns {string}
 */
export function formatUxDuration(ms) {
  const n = Math.max(0, Math.floor(Number(ms) || 0));
  const totalSec = Math.round(n / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
}

/**
 * Aggregate page_dwell by pageKey.
 * @param {object[]} rows
 * @param {{ limitCount?: number }} [opts]
 * @returns {{ pageKey: string, visits: number, totalMs: number, avgMs: number }[]}
 */
export function aggregateUxPageDwell(rows, { limitCount = 25 } = {}) {
  const byKey = new Map();
  (rows || []).forEach((row) => {
    if (row.name !== 'page_dwell') return;
    const pageKey = String(row.pageKey || row.pathname || '').trim();
    if (!pageKey) return;
    const ms = clampDurationMs(row.durationMs) || 0;
    if (ms <= 0) return;
    const prev = byKey.get(pageKey) || { pageKey, visits: 0, totalMs: 0 };
    prev.visits += 1;
    prev.totalMs += ms;
    byKey.set(pageKey, prev);
  });
  return Array.from(byKey.values())
    .map((row) => ({
      ...row,
      avgMs: row.visits ? Math.round(row.totalMs / row.visits) : 0,
    }))
    .sort((a, b) => b.totalMs - a.totalMs || a.pageKey.localeCompare(b.pageKey))
    .slice(0, limitCount);
}

/**
 * Per-user dwell totals from page_dwell rows (AdminHub Users).
 * @param {object[]} rows
 * @param {{ pageLimit?: number }} [opts]
 * @returns {Record<string, { totalMs: number, visits: number, byPage: { pageKey: string, totalMs: number, visits: number }[] }>}
 */
export function aggregateUxDwellByUser(rows, { pageLimit = 5 } = {}) {
  /** @type {Map<string, { totalMs: number, visits: number, pages: Map<string, { pageKey: string, totalMs: number, visits: number }> }>} */
  const byUid = new Map();

  (rows || []).forEach((row) => {
    if (row.name !== 'page_dwell') return;
    const uid = String(row.uid || '').trim();
    if (!uid) return;
    const pageKey = String(row.pageKey || row.pathname || '').trim();
    if (!pageKey) return;
    const ms = clampDurationMs(row.durationMs) || 0;
    if (ms <= 0) return;

    let user = byUid.get(uid);
    if (!user) {
      user = { totalMs: 0, visits: 0, pages: new Map() };
      byUid.set(uid, user);
    }
    user.totalMs += ms;
    user.visits += 1;
    const page = user.pages.get(pageKey) || { pageKey, totalMs: 0, visits: 0 };
    page.totalMs += ms;
    page.visits += 1;
    user.pages.set(pageKey, page);
  });

  /** @type {Record<string, { totalMs: number, visits: number, byPage: { pageKey: string, totalMs: number, visits: number }[] }>} */
  const out = {};
  byUid.forEach((user, uid) => {
    out[uid] = {
      totalMs: user.totalMs,
      visits: user.visits,
      byPage: Array.from(user.pages.values())
        .sort((a, b) => b.totalMs - a.totalMs || a.pageKey.localeCompare(b.pageKey))
        .slice(0, pageLimit),
    };
  });
  return out;
}

/**
 * Recent first-visit exit rows (already newest-first from list).
 * @param {object[]} rows
 * @param {{ limitCount?: number }} [opts]
 * @returns {object[]}
 */
export function listFirstVisitExits(rows, { limitCount = 40 } = {}) {
  return (rows || [])
    .filter((row) => row.name === 'first_visit_exit')
    .slice(0, limitCount);
}
